import type { FlexxiQuote } from "@gaole/shared";
import { fmtDateDMY, rm } from "../../lib/format";

/** Full instalment breakdown for a quote — re-renders whenever the quote (plan/quantity) changes. */
export function FlexxiBreakdownTable({ quote }: { quote: FlexxiQuote }) {
  const firstInstalment = quote.upfrontMode === "FIRST_INSTALLMENT";
  const upfrontInstalment = quote.installments.find((i) => i.dueToday);
  const laterInstalments = quote.installments.filter((i) => !i.dueToday);

  return (
    <div className="space-y-3 rounded-xl border border-brand-pink/25 bg-brand-pink/[0.06] p-3 text-xs">
      <div className="flex items-end justify-between gap-3">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wide text-white/50">Due today</p>
          <p className="font-display text-xl font-bold tabular-nums text-white">{rm(quote.dueToday)}</p>
        </div>
        <span className="rounded-full bg-gradient-to-r from-brand-pink to-brand-blue px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-white">
          {firstInstalment ? "No deposit" : `${quote.depositPercent}% deposit`}
        </span>
      </div>

      <dl className="space-y-1 text-white/65">
        <Row label="Item price" value={rm(quote.principal)} />
        {firstInstalment ? (
          upfrontInstalment ? <Row label="1st instalment (today)" value={rm(upfrontInstalment.amountDue)} /> : null
        ) : (
          <Row label={`Deposit (${quote.depositPercent}%)`} value={rm(quote.depositAmount)} />
        )}
        {quote.flexxiFeeAmount > 0 ? <Row label="FlexxiPlan fee (today)" value={rm(quote.flexxiFeeAmount)} /> : null}
        {quote.interestTotal > 0 ? <Row label="Interest" value={rm(quote.interestTotal)} /> : null}
        <Row label="Total payable" value={rm(quote.totalPayable)} strong />
      </dl>

      <div>
        <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-white/50">
          Then {laterInstalments.length} monthly payment{laterInstalments.length === 1 ? "" : "s"}
        </p>
        <table className="w-full text-left">
          <thead className="text-[10px] uppercase tracking-wide text-white/40">
            <tr>
              <th className="py-1 font-medium">#</th>
              <th className="py-1 font-medium">Due date</th>
              <th className="py-1 text-right font-medium">Amount</th>
            </tr>
          </thead>
          <tbody className="text-white/70">
            {quote.installments.map((inst) => (
              <tr key={inst.sequence} className="border-t border-white/5">
                <td className="py-1 tabular-nums">{inst.sequence}</td>
                <td className="py-1 tabular-nums">{inst.dueToday ? "Today" : fmtDateDMY(inst.dueDate)}</td>
                <td className="py-1 text-right tabular-nums">{rm(inst.amountDue)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className={`flex justify-between gap-2 ${strong ? "border-t border-white/10 pt-1 font-semibold text-white" : ""}`}>
      <dt>{label}</dt>
      <dd className="tabular-nums">{value}</dd>
    </div>
  );
}
